import React, { useState } from 'react';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getApp } from 'firebase/app';

interface ScheduleCallModalProps {
  show: boolean;
  onClose: () => void;
}

// Use existing Firebase app instance (initialized in LoginPage)
const app = getApp();
const db = getFirestore(app);

const ScheduleCallModal: React.FC<ScheduleCallModalProps> = ({ show, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    institutionName: '',
    institutionType: 'school',
    preferredDate: '',
    preferredTime: '',
    topic: 'general',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      // Save to Firestore - this will trigger Cloud Function to send email
      await addDoc(collection(db, 'schedule_call_requests'), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        institutionName: formData.institutionName,
        institutionType: formData.institutionType,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        topic: formData.topic,
        message: formData.message || 'No message provided',
        timestamp: serverTimestamp(),
        status: 'new'
      });

      setSubmitted(true);

      // Reset form after 2 seconds and close modal
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          institutionName: '',
          institutionType: 'school',
          preferredDate: '',
          preferredTime: '',
          topic: 'general',
          message: ''
        });
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Sorry, there was an error scheduling your call. Please try again or contact us directly at contact@mylearningboard.com');
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        style={{ zIndex: 1050 }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header bg-primary-custom text-white">
              <h5 className="modal-title">
                <i className="bi bi-calendar-check me-2"></i>
                Schedule a Call
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
              ></button>
            </div>

            <div className="modal-body">
              {submitted ? (
                <div className="text-center py-5">
                  <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                  <h4 className="mt-3 text-success">Call Scheduled Successfully!</h4>
                  <p className="text-muted">Our team will contact you shortly to confirm the details.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    {/* Personal Information */}
                    <div className="col-12">
                      <h6 className="fw-bold text-primary-custom mb-3">
                        <i className="bi bi-person-fill me-2"></i>
                        Personal Information
                      </h6>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Full Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Email <span className="text-danger">*</span></label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                      <input
                        type="tel"
                        className="form-control"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>

                    {/* Institution Information */}
                    <div className="col-12 mt-4">
                      <h6 className="fw-bold text-primary-custom mb-3">
                        <i className="bi bi-building me-2"></i>
                        Institution Information
                      </h6>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Institution Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        name="institutionName"
                        value={formData.institutionName}
                        onChange={handleChange}
                        required
                        placeholder="Enter institution name"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Institution Type <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        name="institutionType"
                        value={formData.institutionType}
                        onChange={handleChange}
                        required
                      >
                        <option value="school">School</option>
                        <option value="college">College</option>
                        <option value="university">University</option>
                        <option value="coaching">Coaching Centre</option>
                        <option value="preschool">Preschool</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    {/* Scheduling Information */}
                    <div className="col-12 mt-4">
                      <h6 className="fw-bold text-primary-custom mb-3">
                        <i className="bi bi-clock me-2"></i>
                        Preferred Schedule
                      </h6>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Preferred Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className="form-control"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleChange}
                        required
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Preferred Time <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select time slot</option>
                        <option value="09:00-10:00">09:00 AM - 10:00 AM</option>
                        <option value="10:00-11:00">10:00 AM - 11:00 AM</option>
                        <option value="11:00-12:00">11:00 AM - 12:00 PM</option>
                        <option value="12:00-13:00">12:00 PM - 01:00 PM</option>
                        <option value="14:00-15:00">02:00 PM - 03:00 PM</option>
                        <option value="15:00-16:00">03:00 PM - 04:00 PM</option>
                        <option value="16:00-17:00">04:00 PM - 05:00 PM</option>
                        <option value="17:00-18:00">05:00 PM - 06:00 PM</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="form-label">Topic of Discussion <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        name="topic"
                        value={formData.topic}
                        onChange={handleChange}
                        required
                      >
                        <option value="general">General Inquiry</option>
                        <option value="demo">Product Demo</option>
                        <option value="pricing">Pricing Discussion</option>
                        <option value="features">Feature Details</option>
                        <option value="implementation">Implementation Process</option>
                        <option value="migration">Migration from Existing System</option>
                        <option value="technical">Technical Questions</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="form-label">Additional Message (Optional)</label>
                      <textarea
                        className="form-control"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={3}
                        placeholder="Any specific questions or requirements you'd like to discuss..."
                      ></textarea>
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-4">
                    <button type="submit" className="btn btn-primary-custom text-white flex-grow-1" disabled={submitting}>
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Scheduling...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          Schedule Call
                        </>
                      )}
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScheduleCallModal;
